import { Plan } from "../../../@types";
import { BenefitData } from "../components/benefitHelper";

export interface MMPBenefitData {
  label: string;
  category: string;
  benefit: string;
}

export interface MoreBenefitData {
  label: string;
  category: string;
  benefit: string;
  defaultToNotCovered?: boolean;
}

export interface SpecialBenefitData {
  label: string;
  category: string;
  benefit: string;
}

// MMP Benefits
// plan_card.additional_supplemental_benefits.mmp_benefits
export const getAdditionalBenefitsForMMP = (
  t: (id: string) => string
): MMPBenefitData[] => {
  return [
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.non_medicare_covered_prosthetics_medical_supplies"
      ),
      category: "prosthetics_medical_supplies",
      benefit: "non_medicare_covered_prosthetics_medical_supplies",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.early_and_periodic_screening"
      ),
      category: "additional_services",
      benefit: "early_and_periodic_screening",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.tobacco_cessation_counseling_for_pregnant_women"
      ),
      category: "additional_services",
      benefit: "tobacco_cessation_counseling_for_pregnant_women",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.freestanding_birth_center_services"
      ),
      category: "additional_services",
      benefit: "freestanding_birth_center_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.respiratory_care_services"
      ),
      category: "additional_services",
      benefit: "respiratory_care_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.family_planning_services"
      ),
      category: "additional_services",
      benefit: "family_planning_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.nursing_home_services"
      ),
      category: "additional_services",
      benefit: "nursing_home_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.home_and_community_based_services"
      ),
      category: "additional_services",
      benefit: "home_and_community_based_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.personal_care_services"
      ),
      category: "additional_services",
      benefit: "personal_care_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.self_directed_personal_assistance_services"
      ),
      category: "additional_services",
      benefit: "self_directed_personal_assistance_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.private_duty_nursing_services"
      ),
      category: "additional_services",
      benefit: "private_duty_nursing_services",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.case_management_long_term_care"
      ),
      category: "additional_services",
      benefit: "case_management_long_term_care",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.institution_for_mental_disease_services_for_65_or_older"
      ),
      category: "additional_services",
      benefit: "institution_for_mental_disease_services_for_65_or_older",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.services_in_an_intermediate_care_facility_for_disabilities"
      ),
      category: "additional_services",
      benefit: "services_in_an_intermediate_care_facility_for_disabilities",
    },
    {
      label: t(
        "plan_terms.mmp_additional_benefits_and_services.case_management"
      ),
      category: "additional_services",
      benefit: "case_management",
    },
  ];
};

// Benefits
// plan_card.package_benefits.BENEFIT_COMPREHENSIVE_DENTAL
export const getComprehensiveDentalBenefits = (
  t: (id: string) => string
): BenefitData[] => {
  return [
    {
      label: t("plan_terms.comprehensive_dental.nonroutine_services"),
      service: "nonroutine_services",
    },
    {
      label: t("plan_terms.comprehensive_dental.diagnostic_services"),
      service: "diagnostic_services",
    },
    {
      label: t("plan_terms.comprehensive_dental.restorative_services"),
      service: "restorative_services",
    },
    {
      label: t("plan_terms.comprehensive_dental.endodontics"),
      service: "endodontics",
    },
    {
      label: t("plan_terms.comprehensive_dental.periodontics"),
      service: "periodontics",
    },
    {
      label: t("plan_terms.comprehensive_dental.extractions"),
      service: "extractions",
    },
    {
      label: t(
        "plan_terms.comprehensive_dental.prosthodontics_and_other_services"
      ),
      service: "prosthodontics_and_other_services",
    },
  ];
};

// plan_card.package_benefits.BENEFIT_HEARING
export const getHearingBenefits = (
  plan: Plan,
  t: (id: string) => string
): BenefitData[] => {
  let hearingBenefits = [
    {
      label: t("plan_terms.hearing.exam"),
      service: "hearing_exam",
    },
    {
      label: t("plan_terms.hearing.fitting_evaluation"),
      service: "fitting_evaluation",
    },
  ];
  if (
    plan.package_benefits.BENEFIT_HEARING.benefit_services.SERVICE_HEARING_AIDS
  ) {
    hearingBenefits.push({
      label: t("plan_terms.hearing.aids"),
      service: "hearing_aids",
    });
  } else {
    hearingBenefits = [
      ...hearingBenefits,
      {
        label: t("plan_terms.hearing.inner_ear"),
        service: "hearing_aids_inner_ear",
      },
      {
        label: t("plan_terms.hearing.outer_ear"),
        service: "hearing_aids_outer_ear",
      },
      {
        label: t("plan_terms.hearing.over_the_ear"),
        service: "hearing_aids_over_the_ear",
      },
    ];
  }
  return hearingBenefits;
};

// plan_card.package_benefits.BENEFIT_PREVENTIVE_DENTAL
export const getPreventiveDentalBenefits = (
  t: (id: string) => string
): BenefitData[] => {
  return [
    {
      label: t("plan_terms.preventive_dental.oral_exam"),
      service: "oral_exam",
    },
    {
      label: t("plan_terms.preventive_dental.cleaning"),
      service: "cleaning",
    },
    {
      label: t("plan_terms.preventive_dental.flouride_treatment"),
      service: "fluoride_treatment",
    },
    {
      label: t("plan_terms.preventive_dental.dental_xrays"),
      service: "dental_xrays",
    },
  ];
};

// plan_card.package_benefits.BENEFIT_VISION
export const getVisionBenefits = (t: (id: string) => string): BenefitData[] => {
  return [
    {
      label: t("plan_terms.vision.routine_eye_exam"),
      service: "routine_eye_exam",
    },
    {
      label: t("plan_terms.vision.contact_lenses"),
      service: "contact_lenses",
    },
    {
      label: t("plan_terms.vision.eyeglasses"),
      service: "eyeglasses_frames_and_lenses",
    },
    {
      label: t("plan_terms.vision.eyeglass_frames_only"),
      service: "eyeglass_frames",
    },
    {
      label: t("plan_terms.vision.eyeglass_lenses_only"),
      service: "eyeglass_lenses",
    },
    {
      label: t("plan_terms.vision.upgrades"),
      service: "upgrades",
    },
  ];
};

// More Benefits
// plan_card.additional_supplemental_benefits.other_benefits
export const getMoreBenefits = (
  t: (id: string) => string
): MoreBenefitData[] => {
  return [
    {
      label: t("more_benefits.fitness"),
      category: "defined_supplemental_benefits",
      benefit: "fitness_benefit",
    },
    {
      label: t("more_benefits.transportation_non_emergency_any_health"),
      category: "transportation_services",
      benefit: "any_health_related_location",
    },
    {
      label: t("more_benefits.transportation_non_emergency_plan_approved"),
      category: "transportation_services",
      benefit: "plan_approved_health_related_location",
    },
    {
      label: t("more_benefits.over_the_counter_drugs"),
      category: "otc_items",
      benefit: "otc_items",
    },
    {
      label: t("more_benefits.in_home_support"),
      category: "defined_supplemental_benefits",
      benefit: "in_home_support_services",
    },
    {
      label: t("more_benefits.worldwide_emergency"),
      category: "worldwide_emergency_transportation",
      benefit: "worldwide_emergency_coverage",
    },
    {
      label: t("more_benefits.worldwide_urgent"),
      category: "worldwide_emergency_transportation",
      benefit: "worldwide_urgent_care",
    },
    {
      label: t("more_benefits.response_device"),
      category: "defined_supplemental_benefits",
      benefit: "personal_emergency_response_sys_pers",
    },
    {
      label: t("more_benefits.chiropractic_service"),
      category: "chiropractic_services",
      benefit: "routine_care",
    },
    {
      label: t("more_benefits.bathroom_safety_devices"),
      category: "defined_supplemental_benefits",
      benefit: "bathroom_safety_devices",
    },
    {
      label: t("more_benefits.meals"),
      category: "meal_benefit",
      benefit: "meal_benefit",
    },

    {
      label: t("more_benefits.annual_physical"),
      category: "annual_physical_exam",
      benefit: "annual_physical_exam",
    },
    {
      label: t("more_benefits.telehealth"),
      category: "additional_telehealth_services",
      benefit: "medicare_covered_visits",
    },
    {
      label: t("more_benefits.worldwide_emergency"),
      category: "worldwide_emergency_urgent_coverage",
      benefit: "worldwide_emergency_coverage",
      defaultToNotCovered: true,
    },
  ];
};

export const getNonOpioidBenefits = (
  t: (id: string) => string
): MoreBenefitData[] => {
  return [
    {
      label: t("plan_terms.chiropractic_services"),
      category: "chiropractic_services",
      benefit: "routine_care",
      defaultToNotCovered: true,
    },
    {
      label: t("plan_terms.acupuncture"),
      category: "non_opioid_pain_management",
      benefit: "acupuncture",
      defaultToNotCovered: true,
    },
    {
      label: t("plan_terms.massage_therapy"),
      category: "defined_supplemental_benefits",
      benefit: "therapeutic_massage",
      defaultToNotCovered: true,
    },
    {
      label: t("plan_terms.alternative_therapies"),
      category: "defined_supplemental_benefits",
      benefit: "alternative_therapies",
      defaultToNotCovered: true,
    },
  ];
};

// Special Benefits
// plan_card.additional_supplemental_benefits.special_benefits
export const getSpecialBenefits = (
  t: (id: string) => string
): SpecialBenefitData[] => {
  return [
    {
      label: t("special_benefit.food_and_produce"),
      category: "non_primarily_health_related_benefits",
      benefit: "food_and_produce",
    },
    {
      label: t("special_benefit.meals"),
      category: "non_primarily_health_related_benefits",
      benefit: "meals_beyond_limited_basis",
    },
    {
      label: t("special_benefit.pest_control"),
      category: "non_primarily_health_related_benefits",
      benefit: "pest_control",
    },
    {
      label: t("special_benefit.transportation_non_medical"),
      category: "non_primarily_health_related_benefits",
      benefit: "transportation_for_non_medical_needs",
    },
    {
      label: t("special_benefit.air_quality"),
      category: "non_primarily_health_related_benefits",
      benefit: "indoor_air_quality_equipment_and_services",
    },
    {
      label: t("special_benefit.social_needs"),
      category: "non_primarily_health_related_benefits",
      benefit: "social_needs_benefit",
    },
    {
      label: t("special_benefit.complementary_therapies"),
      category: "non_primarily_health_related_benefits",
      benefit: "complementary_therapies",
    },
    {
      label: t("special_benefit.self_direction"),
      category: "non_primarily_health_related_benefits",
      benefit: "services_supporting_self_direction",
    },
    {
      label: t("special_benefit.home_modifications"),
      category: "non_primarily_health_related_benefits",
      benefit: "structural_home_modifications",
    },
    {
      label: t("special_benefit.general_supports"),
      category: "non_primarily_health_related_benefits",
      benefit: "transitional_temporary_supports",
    },
    {
      label: t("special_benefit.reduced_cost_sharing"),
      category: "reduced_cost_sharing_for_vbid_uf_ssbci",
      benefit: "reduced_cost_sharing_for_vbid_uf_ssbci",
    },
  ];
};
